import { Capacitor } from "@capacitor/core";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";
import { mergeRefs } from "react-merge-refs";

interface Props extends React.AriaAttributes {
  inputProps: Omit<JSX.IntrinsicElements["input"], "value"> & { value: string };
}

const SMSCodeInput = (props: Props) => {
  const innerInputRef = React.useRef<HTMLInputElement>(null);

  const inputRef =
    props.inputProps.ref !== undefined
      ? mergeRefs([props.inputProps.ref, innerInputRef])
      : innerInputRef;

  const handleClick = () => {
    focusActualInputOnWeb();
  };

  React.useEffect(() => {
    focusActualInputOnWeb();
  });

  const focusActualInputOnWeb = React.useCallback(() => {
    if (innerInputRef.current !== null && Capacitor.getPlatform() === "web") {
      innerInputRef.current.focus();
      innerInputRef.current.setSelectionRange(
        innerInputRef.current.value.length,
        innerInputRef.current.value.length
      );
    }
  }, []);

  const { inputProps, ...rest } = props;

  return (
    <SMSCodeInput.Root onClick={handleClick} {...rest}>
      {[0, 1, 2, 3, 4].map((i) => (
        <SMSCodeInput.Box
          key={i}
          aria-invalid={props["aria-invalid"]}
          filled={props.inputProps.value.length > i}
        >
          {props.inputProps.value[i]}
        </SMSCodeInput.Box>
      ))}
      <SMSCodeInput.Input {...props.inputProps} ref={inputRef} />
    </SMSCodeInput.Root>
  );
};

SMSCodeInput.Input = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
`;
SMSCodeInput.Box = styled.div<{ filled: boolean }>`
  --size: 11vw;
  --max-size: 48px;
  border: 2px solid var(--gray-100);
  border-radius: 8px;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  width: var(--size);
  height: var(--size);
  max-width: var(--max-size);
  max-height: var(--max-size);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 250ms ease;

  &[aria-invalid="true"] {
    border-color: var(--red);
  }

  ${(props) =>
    props.filled
      ? css`
          border-color: var(--primary-color);
          transform: translateY(-15%);
        `
      : css``}
`;

SMSCodeInput.Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

export default SMSCodeInput;
